import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Form, Input, Radio, Row, Select, Textarea } from 'tdesign-react';
import Text from '@/components/Text';
import Tip from '@/components/Tip';
import { clusterList, createCluster, getClusterDetail } from '@/services/resourceService';
import {
  QUICK_DEPLOY_STEPS,
  setQuickDeployClusterInfo,
  setQuickDeployStep,
} from '@/slices/clusterSlice';
import { isVoid, toTsOptions } from '@/utils/common';
import formRulePattern from '@/utils/formRulePattern';
import TextSelect from '@/components/TextSelect';

const { FormItem } = Form;

function ChoiceClusterStep({
  setQuickDeployStep,
  envOptions,
  clusterType,
  setQuickDeployClusterInfo,
  stencilOptions,
}) {
  const [form] = Form.useForm();
  const [clusterOptions, setClusterOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const selectCluster = Form.useWatch('selectCluster', form);
  const opsEnvId = Form.useWatch('opsEnvId', form);

  const resetClusterInfo = () => {
    form.reset({
      fields: [
        'cluster',
        'name',
        'key',
        'clusterInstallPath',
        'gvarHostInstallUser',
        'desc',
        'varStencilId',
      ],
    });
  };

  const envChange = async (id) => {
    resetClusterInfo();
    form.setFields([{ name: 'selectCluster', value: 'choiceExists' }]);
    const { data } = await clusterList({
      envId: id,
      pageSize: 99999,
      startIndex: 1,
      type: clusterType,
    });
    setClusterOptions(
      data.items.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    );
  };

  const clusterChange = async (id) => {
    const { data } = await getClusterDetail(id);
    form.setFieldsValue({
      name: data.name,
      key: data.key,
      clusterInstallPath: data.clusterInstallPath,
      gvarHostInstallUser: data.gvarHostInstallUser,
      varStencilId: data.varStencilId,
      desc: data.desc,
    });
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      const cache = { ...fields };
      if (selectCluster === 'createNew') {
        setSubmitting(true);
        try {
          const { data } = await createCluster({
            ...fields,
            type: clusterType,
          });
          cache.cluster = data;
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          setSubmitting(false);
        }
      }
      setQuickDeployClusterInfo(cache);
      setQuickDeployStep(QUICK_DEPLOY_STEPS.MAINTAIN_HOSTS);
    }
  };

  const selectClusterChange = (value) => {
    resetClusterInfo();
    if (value === 'createNew') {
      // td bug
      setTimeout(() => {
        form.setFieldsValue({
          clusterInstallPath: '/app/pulsar',
          gvarHostInstallUser: 'pulsar',
        });
      }, 50);
    }
  };
  return (
    <Root>
      <Form
        form={form}
        labelWidth={110}
        style={{ width: '80%' }}
        initialData={{ selectCluster: 'choiceExists' }}
        onSubmit={handleSubmit}
      >
        <FormItem
          name='opsEnvId'
          label={
            <Tip content='环境表示一个独立的网络区域，一般在管理员部署阶段初始化，如果没有找到的你需要的环境，请联系管理员创建'>
              选择环境
            </Tip>
          }
          rules={[{ required: true }]}
        >
          <Select options={toTsOptions(envOptions)} onChange={envChange} />
        </FormItem>
        {!isVoid(opsEnvId) && (
          <>
            <FormItem
              name='selectCluster'
              label={
                <Tip content='集群表示需要创建的Pulsar集群，集群名称则为Plusar cluster名称，如果在下拉菜单中没有找到集群，您可新建一个集群'>
                  选择
                </Tip>
              }
            >
              <Radio.Group onChange={selectClusterChange}>
                <Radio value='choiceExists'>现有集群</Radio>
                <Radio value='createNew'>新建集群</Radio>
              </Radio.Group>
            </FormItem>
            {selectCluster === 'createNew' ? (
              <>
                <FormItem name='name' label='集群名称' rules={[{ required: true }]}>
                  <Input />
                </FormItem>

                <FormItem
                  rules={[{ required: true, message: '唯一标识符必填' }, formRulePattern('name')]}
                  name='key'
                  label={
                    <Tip content='用于系统使用集群名称，同pular的cluster_name'>唯一标识符</Tip>
                  }
                >
                  <Input />
                </FormItem>
                <FormItem name='clusterInstallPath' label='基础路径' rules={[{ required: true }]}>
                  <Input />
                </FormItem>
                <FormItem name='gvarHostInstallUser' label='部署用户' rules={[{ required: true }]}>
                  <Input />
                </FormItem>
                <FormItem name='varStencilId' label='变量模板' rules={[{ required: true }]}>
                  <Select options={toTsOptions(stencilOptions)} />
                </FormItem>
                <FormItem name='desc' label='集群说明' rules={[{ required: true }]}>
                  <Textarea placeholder='集群说明' />
                </FormItem>
              </>
            ) : (
              <>
                <FormItem name='cluster' label='选择集群' rules={[{ required: true }]}>
                  <Select options={toTsOptions(clusterOptions)} onChange={clusterChange} />
                </FormItem>
                <FormItem name='name' label='集群名称'>
                  <Text />
                </FormItem>
                <FormItem name='key' label='唯一标识'>
                  <Text />
                </FormItem>
                <FormItem name='clusterInstallPath' label='基础路径'>
                  <Text />
                </FormItem>
                <FormItem name='gvarHostInstallUser' label='部署用户'>
                  <Text />
                </FormItem>
                <FormItem name='varStencilId' label='变量模板'>
                  <TextSelect options={stencilOptions} />
                </FormItem>
                <FormItem name='desc' label='集群说明'>
                  <Text />
                </FormItem>
              </>
            )}
          </>
        )}
      </Form>
      <Row justify='end' style={{ marginTop: 20 }}>
        <Button loading={submitting} onClick={() => form.submit()}>
          下一步
        </Button>
      </Row>
    </Root>
  );
}

export default connect(
  (state) => ({
    envOptions: state.resource.envOptions,
    clusterType: state.cluster.quickDeploy.clusterType,
    stencilOptions: state.variable.stencilList?.map((item) => ({
      label: item.stencilName,
      value: item.id,
    })),
  }),
  {
    setQuickDeployStep,
    setQuickDeployClusterInfo,
  },
)(ChoiceClusterStep);

const Root = styled.div`
  margin-top: 20px;
`;
